'use client'

import Link from 'next/link'
import Image from 'next/image'
import {
  Radio,
  Clock,
  Music,
  Video,
  Lock,
  Send,
  Mail,
  Mic,
  PartyPopper,
  Building2,
  Palette,
  ChevronRight,
  Play,
  MessageSquare,
  Gift,
} from 'lucide-react'
import { useMetrica } from 'next-yandex-metrica'
import Header from '@/components/header'
import Footer from '@/components/footer'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel'

const features = [
  {
    icon: <Clock className="w-5 h-5" />,
    title: '2 часа живого эфира',
    description: 'Полноценный стрим с профессиональным сведением и подачей',
  },
  {
    icon: <Music className="w-5 h-5" />,
    title: 'Ваши треки',
    description: 'Выбирайте музыку, заказывайте приветы и шаутауты',
  },
  {
    icon: <Lock className="w-5 h-5" />,
    title: 'Приватный или публичный',
    description: 'Закрытый формат только для вас или открытый для всех',
  },
  {
    icon: <Video className="w-5 h-5" />,
    title: 'Мультиплатформа',
    description: 'YouTube, Twitch, VK, RuTube — на любых площадках',
  },
  {
    icon: <MessageSquare className="w-5 h-5" />,
    title: 'Живое общение',
    description: 'Интерактив с ведущим, комментарии и обсуждение треков',
  },
  {
    icon: <Gift className="w-5 h-5" />,
    title: 'Запись эфира',
    description: 'Получите полную запись стрима после окончания',
  },
]

const useCases = [
  {
    icon: <Mic className="w-7 h-7" />,
    title: 'Промо артиста',
    description:
      'Посвятите целый стрим вашей музыке. Саня разберёт треки, даст фидбек в эфире и познакомит аудиторию с вашим звучанием. Отличный формат для релизов и продвижения.',
    color: 'from-blue-600/20 to-blue-400/5',
    borderColor: 'border-blue-500/20',
    iconBg: 'bg-blue-500/10',
  },
  {
    icon: <PartyPopper className="w-7 h-7" />,
    title: 'День рождения / Личный стрим',
    description:
      'Персональный стрим для вас и друзей. Любимые треки, приветы, атмосфера праздника. Приватный или публичный — как пожелаете.',
    color: 'from-purple-600/20 to-purple-400/5',
    borderColor: 'border-purple-500/20',
    iconBg: 'bg-purple-500/10',
  },
  {
    icon: <Building2 className="w-7 h-7" />,
    title: 'Бренд / Лейбл / Мероприятие',
    description:
      'Продвижение через живой DnB-эфир. Подходит для лейблов, брендов и организаторов вечеринок, которые хотят охватить аудиторию любителей электронной музыки.',
    color: 'from-emerald-600/20 to-emerald-400/5',
    borderColor: 'border-emerald-500/20',
    iconBg: 'bg-emerald-500/10',
  },
  {
    icon: <Palette className="w-7 h-7" />,
    title: 'Свой формат',
    description:
      'Полностью кастомный стрим: выберите поджанр, настроение, тему. От лёгкого Liquid до жёсткого Neurofunk — ваш стрим, ваши правила.',
    color: 'from-orange-600/20 to-orange-400/5',
    borderColor: 'border-orange-500/20',
    iconBg: 'bg-orange-500/10',
  },
]

const steps = [
  {
    number: '01',
    title: 'Свяжитесь с нами',
    description: 'Напишите в Telegram или на Email',
    icon: <Send className="w-5 h-5" />,
  },
  {
    number: '02',
    title: 'Обсудим детали',
    description: 'Формат, треки, дата, платформа трансляции',
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    number: '03',
    title: 'Стрим в назначенное время',
    description: '2 часа живого эфира для вас',
    icon: <Radio className="w-5 h-5" />,
  },
  {
    number: '04',
    title: 'Получите запись',
    description: 'Полная запись стрима — ваша навсегда',
    icon: <Video className="w-5 h-5" />,
  },
]

const exampleStreams = [
  { id: '7oZL-y2FH_4', title: '' },
  { id: 'DmCrjK2i9bw', title: '' },
  { id: '_WA3MWNIo1c', title: '' },
  { id: '3_ej4axiGzE', title: '' },
]

const YOUTUBE_PLAYLIST_URL = 'https://www.youtube.com/playlist?list=PLONjfPEXt3If7rFCjF328H0q9IQ8X9NNj'

export default function StreamOnDemandPage() {
  const { reachGoal } = useMetrica()

  return (
    <main className="min-h-screen flex flex-col pt-[76px] bg-radio-dark">
      <Header />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-radio-dark-blue via-black to-radio-dark-blue"></div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-radio-blue/6 blur-[150px]"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-purple-500/4 blur-[120px]"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center mb-8">
              <div className="w-16 h-[2px] bg-gradient-to-r from-transparent to-radio-blue/40 rounded-full"></div>
              <div className="relative mx-5">
                <Radio className="text-radio-blue z-10 relative w-7 h-7" />
                <div className="absolute inset-0 bg-radio-blue/20 rounded-full blur-lg animate-pulse"></div>
              </div>
              <div className="w-16 h-[2px] bg-gradient-to-l from-transparent to-radio-blue/40 rounded-full"></div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display text-white mb-8 leading-tight">
              Закажи{' '}
              <span className="text-radio-blue" style={{ textShadow: '0 0 40px rgba(30,174,215,0.35)' }}>
                персональный стрим
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Полноценный 2-х часовой Drum&Bass эфир по вашему вкусу и настроению.
              Выбирайте треки, заказывайте приветы и создавайте свою особую атмосферу вместе с Саней.
            </p>

            <div className="mb-10">
              <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-2xl px-8 py-4 border border-radio-blue/20">
                <span className="text-gray-400 text-lg">от</span>
                <span className="text-3xl md:text-4xl font-bold text-radio-blue" style={{ textShadow: '0 0 20px rgba(30,174,215,0.3)' }}>
                  20 000 &#8381;
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="https://t.me/nuvertal"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => reachGoal('stream-on-demand-hero-telegram-click')}
                className="inline-flex items-center gap-2.5 bg-gradient-to-r from-radio-blue to-cyan-500 hover:from-radio-blue/90 hover:to-cyan-500/90 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_0_25px_rgba(30,174,215,0.25)] hover:shadow-[0_0_35px_rgba(30,174,215,0.4)] hover:-translate-y-0.5"
              >
                <Send className="w-5 h-5" />
                Написать в Telegram
              </Link>
              <Link
                href="mailto:contact@radiosanya.ru?subject=%D0%97%D0%B0%D0%BA%D0%B0%D0%B7%20%D0%BF%D0%B5%D1%80%D1%81%D0%BE%D0%BD%D0%B0%D0%BB%D1%8C%D0%BD%D0%BE%D0%B3%D0%BE%20%D1%81%D1%82%D1%80%D0%B8%D0%BC%D0%B0"
                onClick={() => reachGoal('stream-on-demand-hero-email-click')}
                className="inline-flex items-center gap-2.5 bg-white/5 hover:bg-white/10 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 border border-white/15 hover:border-radio-blue/40 hover:-translate-y-0.5 backdrop-blur-sm"
              >
                <Mail className="w-5 h-5" />
                Написать на Email
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get — horizontal icon strip with accent left border */}
      <section className="py-20 md:py-24 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-radio-dark-blue/80 via-radio-dark to-radio-dark"></div>
        {/* Diagonal accent line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-radio-blue/20 to-transparent"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14">
              <div>
                <p className="text-radio-blue/60 uppercase tracking-[0.2em] text-sm font-medium mb-3">Что вы получите</p>
                <h2 className="text-3xl md:text-4xl font-display text-white leading-tight">
                  Всё включено в <span className="text-radio-blue">один стрим</span>
                </h2>
              </div>
              <div className="hidden md:block w-48 h-px bg-gradient-to-r from-radio-blue/30 to-transparent mt-4"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-px bg-white/5 rounded-2xl overflow-hidden">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className="bg-radio-dark/90 p-7 relative group hover:bg-radio-dark-blue/50 transition-colors duration-500"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-radio-blue/0 via-radio-blue/40 to-radio-blue/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-radio-blue/10 flex items-center justify-center text-radio-blue flex-shrink-0 group-hover:bg-radio-blue/20 group-hover:shadow-[0_0_20px_rgba(30,174,215,0.15)] transition-all duration-500">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1.5">{feature.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed group-hover:text-gray-400 transition-colors">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases — asymmetric layout with big number accents */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-bl from-black via-radio-dark-blue/40 to-black"></div>
        {/* Ambient orbs */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-500/4 blur-[130px]"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-purple-500/4 blur-[130px]"></div>
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-radio-blue/60 uppercase tracking-[0.2em] text-sm font-medium mb-3">Сценарии использования</p>
              <h2 className="text-3xl md:text-4xl font-display text-white">
                Для кого <span className="text-radio-blue">персональный стрим</span>
              </h2>
            </div>

            <div className="space-y-5">
              {useCases.map((useCase, i) => (
                <div
                  key={i}
                  className="group relative"
                >
                  <div className={`relative flex flex-col md:flex-row items-start gap-6 md:gap-8 bg-gradient-to-r ${useCase.color} backdrop-blur-md rounded-2xl p-7 md:p-8 border ${useCase.borderColor} overflow-hidden hover:shadow-[0_0_40px_rgba(30,174,215,0.08)] transition-all duration-500`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    <div className={`w-14 h-14 rounded-2xl ${useCase.iconBg} flex items-center justify-center text-radio-blue flex-shrink-0 relative z-10 group-hover:shadow-[0_0_20px_rgba(30,174,215,0.2)] transition-shadow duration-500`}>
                      {useCase.icon}
                    </div>
                    <div className="relative z-10">
                      <h3 className="text-xl font-semibold text-white mb-2">{useCase.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed max-w-xl">{useCase.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Examples — full-bleed dark section with carousel */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-radio-dark"></div>
        {/* Subtle top/bottom border glow */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        {/* Centered glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-radio-blue/4 blur-[120px]"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14">
              <div>
                <p className="text-radio-blue/60 uppercase tracking-[0.2em] text-sm font-medium mb-3">Портфолио</p>
                <h2 className="text-3xl md:text-4xl font-display text-white leading-tight">
                  Примеры <span className="text-radio-blue">наших стримов</span>
                </h2>
                <p className="text-gray-500 mt-3 max-w-md">
                  Ваш персональный стрим будет на том же уровне — только полностью под ваш вкус.
                </p>
              </div>
              <Link
                href={YOUTUBE_PLAYLIST_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => reachGoal('stream-on-demand-all-streams-click')}
                className="hidden md:inline-flex items-center gap-2 text-radio-blue hover:text-white transition-colors font-medium text-sm mt-4 md:mt-0"
              >
                Все стримы на YouTube
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="sm:px-12">
              <Carousel
                opts={{
                  align: 'start',
                  loop: true,
                }}
              >
                <CarouselContent className="-ml-3 md:-ml-5">
                  {exampleStreams.map((stream) => (
                    <CarouselItem key={stream.id} className="pl-3 md:pl-5 sm:basis-1/2">
                      <Link
                        href={`https://www.youtube.com/watch?v=${stream.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => reachGoal('stream-on-demand-example-click')}
                        className="group/card block rounded-2xl overflow-hidden border border-white/[0.06] shadow-2xl shadow-black/40 hover:border-radio-blue/20 transition-colors duration-500"
                      >
                        <div className="aspect-video relative bg-black">
                          <Image
                            src={`https://img.youtube.com/vi/${stream.id}/maxresdefault.jpg`}
                            alt="Stream preview"
                            fill
                            className="object-cover group-hover/card:scale-105 transition-transform duration-700"
                            sizes="(max-width: 640px) 100vw, 50vw"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover/card:bg-black/10 transition-colors duration-500"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl group-hover/card:bg-radio-blue/80 group-hover/card:border-radio-blue/50 group-hover/card:scale-110 transition-all duration-300">
                              <Play className="w-7 h-7 text-white ml-0.5" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="flex -left-3 sm:-left-10 bg-black/50 sm:bg-white/5 backdrop-blur-md border-white/10 text-white hover:bg-radio-blue/20 hover:border-radio-blue/30 hover:text-radio-blue" />
                <CarouselNext className="flex -right-3 sm:-right-10 bg-black/50 sm:bg-white/5 backdrop-blur-md border-white/10 text-white hover:bg-radio-blue/20 hover:border-radio-blue/30 hover:text-radio-blue" />
              </Carousel>
            </div>

            <div className="text-center mt-8 md:hidden">
              <Link
                href={YOUTUBE_PLAYLIST_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => reachGoal('stream-on-demand-all-streams-click')}
                className="inline-flex items-center gap-2 text-radio-blue hover:text-white transition-colors font-medium text-sm"
              >
                Все стримы на YouTube
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-radio-dark-blue/60 via-radio-dark to-radio-dark"></div>
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-radio-blue/6 blur-[150px]"></div>
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] rounded-full bg-cyan-500/4 blur-[120px]"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-radio-blue/15 to-transparent"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <p className="text-radio-blue/60 uppercase tracking-[0.2em] text-sm font-medium mb-3">Процесс</p>
              <h2 className="text-3xl md:text-4xl font-display text-white">
                Как это <span className="text-radio-blue">работает</span>
              </h2>
            </div>

            {/* All screen sizes — vertical list */}
            <div className="relative max-w-lg mx-auto">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-6 sm:gap-10 group">
                  {/* Left column — number + line */}
                  <div className="flex flex-col items-center w-16 sm:w-20 flex-shrink-0">
                    <span className="text-[40px] sm:text-[56px] font-bold bg-gradient-to-b from-radio-blue to-cyan-400/70 bg-clip-text text-transparent leading-none mb-1 group-hover:from-white group-hover:to-radio-blue transition-all duration-500 select-none">
                      {step.number}
                    </span>
                    {i < steps.length - 1 && (
                      <div className="w-px flex-1 bg-gradient-to-b from-radio-blue/25 to-transparent"></div>
                    )}
                  </div>

                  {/* Right column — content */}
                  <div className={`${i < steps.length - 1 ? 'pb-10 sm:pb-14' : 'pb-0'} pt-1`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-radio-blue [&>svg]:w-5 [&>svg]:h-5 opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                        {step.icon}
                      </div>
                      <h3 className="text-white font-semibold text-xl sm:text-[22px] leading-snug">{step.title}</h3>
                    </div>
                    <p className="text-gray-500 text-[15px] leading-relaxed group-hover:text-gray-400 transition-colors duration-300 pl-8">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — standout section with animated border */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-radio-dark-blue/80 via-radio-dark to-radio-dark"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-radio-blue/6 blur-[180px]"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-radio-blue/15 to-transparent"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Animated border wrapper */}
            <div className="relative">
              <div className="absolute -inset-px bg-gradient-to-r from-radio-blue/30 via-cyan-400/20 to-radio-blue/30 rounded-3xl blur-sm animate-slow-flow"></div>

              <div className="relative bg-gradient-to-br from-radio-dark-blue/80 via-black/90 to-radio-dark-blue/80 backdrop-blur-xl rounded-3xl p-10 md:p-14 border border-radio-blue/10 text-center overflow-hidden">
                {/* Inner glow accents */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-radio-blue/8 rounded-full blur-[80px]"></div>
                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyan-500/5 rounded-full blur-[80px]"></div>

                <div className="relative z-10">
                  <h2 className="text-3xl md:text-4xl font-display text-white mb-3">
                    Готовы заказать стрим?
                  </h2>
                  <p className="text-gray-400 mb-8 leading-relaxed max-w-lg mx-auto">
                    Свяжитесь с нами, обсудим детали и создадим для вас уникальный эфир.
                  </p>

                  <div className="inline-flex items-center gap-3 mb-10 bg-white/5 rounded-2xl px-8 py-4 border border-radio-blue/20">
                    <span className="text-gray-400 text-lg">от</span>
                    <span className="text-3xl md:text-4xl font-bold text-radio-blue" style={{ textShadow: '0 0 20px rgba(30,174,215,0.3)' }}>
                      20 000 &#8381;
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                    <Link
                      href="https://t.me/nuvertal"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => reachGoal('stream-on-demand-cta-telegram-click')}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-radio-blue to-cyan-500 hover:from-radio-blue/90 hover:to-cyan-500/90 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-[0_0_25px_rgba(30,174,215,0.25)] hover:shadow-[0_0_40px_rgba(30,174,215,0.45)] hover:-translate-y-0.5"
                    >
                      <Send className="w-5 h-5" />
                      Telegram — @nuvertal
                    </Link>
                    <Link
                      href="mailto:contact@radiosanya.ru?subject=%D0%97%D0%B0%D0%BA%D0%B0%D0%B7%20%D0%BF%D0%B5%D1%80%D1%81%D0%BE%D0%BD%D0%B0%D0%BB%D1%8C%D0%BD%D0%BE%D0%B3%D0%BE%20%D1%81%D1%82%D1%80%D0%B8%D0%BC%D0%B0"
                      onClick={() => reachGoal('stream-on-demand-cta-email-click')}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-white/5 hover:bg-white/10 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 border border-white/15 hover:border-radio-blue/40 hover:-translate-y-0.5"
                    >
                      <Mail className="w-5 h-5" />
                      contact@radiosanya.ru
                    </Link>
                  </div>
                  <p className="text-gray-500 text-sm">
                    Ответим в течение дня. Расскажите о вашей идее — вместе сделаем крутой эфир.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style jsx global>{`
          @keyframes slow-flow {
            0% { background-position: 0% 50%; }
            100% { background-position: 400% 50%; }
          }
          .animate-slow-flow {
            background-size: 400% 100%;
            animation: slow-flow 20s linear infinite;
          }
        `}</style>
      </section>

      <Footer />
    </main>
  )
}
